// befoe haveing everythingin configdatabase
// src/components/admin/ConfigManagement.js
import {
  AlertCircle,
  BarChart3,
  Check,
  ChevronDown,
  ChevronUp,
  Facebook,
  Globe,
  Info,
  Instagram,
  Layout,
  Linkedin,
  Loader2,
  Mail,
  MapPin,
  DollarSign as Money,
  Palette,
  Phone,
  Save,
  Settings,
  Twitter,
  Type,
  Upload,
  X,
  Youtube
} from "lucide-react";
import { memo, useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "../../utils/axiosHandler";
import ColorPicker from "./shared/ColorPicker";

// Memoized individual form components to prevent unnecessary re-renders
const TextField = memo(
  ({
    label,
    name,
    value,
    onChange,
    placeholder,
    className,
    type = "text",
    required = false,
    helperText = null,
  }) => {
    // Create local state to avoid re-renders in parent component
    const [localValue, setLocalValue] = useState(value || "");

    // Update local state when prop changes
    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      // Only update parent state when field loses focus
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <input
          id={name}
          name={name}
          type={type}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          className={`w-full p-2 border border-gray-300 rounded-md ${className || ""
            }`}
        />
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const IconTextField = memo(
  ({
    label,
    name,
    value,
    onChange,
    icon: Icon,
    className,
    placeholder,
    required = false,
    helperText = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id={name}
            name={name}
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            className={`w-full p-2 pl-10 border border-gray-300 rounded-md ${className || ""
              }`}
          />
        </div>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const TextArea = memo(
  ({
    label,
    name,
    value,
    onChange,
    rows = 3,
    className,
    required = false,
    helperText = null,
    icon: Icon = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <div className={Icon ? "relative" : ""}>
          {Icon && (
            <div className="absolute top-3 left-3 pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
          )}
          <textarea
            id={name}
            name={name}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={rows}
            required={required}
            className={`w-full p-2 ${Icon ? "pl-10" : ""
              } border border-gray-300 rounded-md ${className || ""}`}
          ></textarea>
        </div>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const SelectField = memo(
  ({
    label,
    name,
    value,
    onChange,
    options,
    className,
    required = false,
    helperText = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      // For select fields, we can trigger onChange immediately since there's no typing
      onChange(name, newValue);
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <select
          id={name}
          name={name}
          value={localValue}
          onChange={handleChange}
          required={required}
          className={`w-full p-2 border border-gray-300 rounded-md ${className || ""
            }`}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const Checkbox = memo(
  ({ label, name, checked, onChange, helperText = null }) => {
    const handleChange = (e) => {
      onChange(name, e.target.checked);
    };

    return (
      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            name={name}
            checked={checked || false}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            {label}
          </span>
        </label>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1 ml-6">{helperText}</p>
        )}
      </div>
    );
  }
);

const CurrencyField = memo(
  ({
    label,
    name,
    value,
    onChange,
    min = "0.01",
    step = "0.01",
    className,
    required = false,
    helperText = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500">$</span>
          </div>
          <input
            id={name}
            name={name}
            type="number"
            min={min}
            step={step}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            required={required}
            className={`w-full p-2 pl-8 border border-gray-300 rounded-md ${className || ""
              }`}
          />
        </div>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const ColorField = memo(({ label, colorName, color, onChange }) => {
  // Handle color change and pass it to parent
  const handleColorChange = useCallback(
    (newColor) => {
      onChange(colorName, newColor);
    },
    [onChange, colorName]
  );

  return (
    <div className="flex items-center mb-4">
      <label className="block text-sm font-medium text-gray-700 w-32">
        {label}
      </label>
      <div className="flex-grow">
        <ColorPicker
          color={color || "#ffffff"}
          onChange={handleColorChange}
          allowAlpha={false}
        />
      </div>
    </div>
  );
});

// Configuration section component
const ConfigSection = memo(
  ({ title, icon: Icon, id, isExpanded, onToggle, children }) => (
    <div className="mb-6 bg-white rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={() => onToggle(id)}
        className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 text-left"
        type="button"
      >
        <div className="flex items-center">
          {Icon && <Icon className="w-5 h-5 text-blue-600 mr-2" />}
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 border-t border-gray-200">{children}</div>
      )}
    </div>
  )
);

// File Upload Component
const FileUploadField = memo(({
  label,
  name,
  value,
  onChange,
  accept = "image/*",
  helperText = null,
  required = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value);

  // Update preview when value changes
  useEffect(() => {
    setPreview(value);
  }, [value]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Determine file type based on the field name
      let fileType = '';
      if (name === 'logoUrl') {
        fileType = 'logo';
      } else if (name === 'faviconUrl') {
        fileType = 'favicon';
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/config/upload?fileType=${fileType}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const fileUrl = response.data.url;
        console.log('Upload successful:', fileUrl);
        setPreview(fileUrl);
        onChange(name, fileUrl);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setPreview('');
    onChange(name, '');
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && "*"}
      </label>

      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-32 h-32 object-cover rounded-md border"
          />
          <button
            type="button"
            onClick={removeFile}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-2">
            <label htmlFor={`file-${name}`} className="cursor-pointer">
              <span className="text-blue-600 hover:text-blue-500">
                {uploading ? 'Uploading...' : 'Click to upload'}
              </span>
              <input
                id={`file-${name}`}
                type="file"
                accept={accept}
                onChange={handleFileChange}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {accept === "image/*" ? "PNG, JPG, GIF, WebP, ICO up to 5MB" : "File upload"}
          </p>
        </div>
      )}

      {helperText && (
        <p className="text-xs text-gray-500 mt-1">{helperText}</p>
      )}
    </div>
  );
});






// Main component
const ConfigManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedSection, setExpandedSection] = useState("general");


  const [editingEncryptedFields, setEditingEncryptedFields] = useState({});
  const [encryptedFieldValues, setEncryptedFieldValues] = useState({});

  // Individual encrypted field save functions
  const saveEncryptedField = async (endpoint, fieldValue, fieldName, successMessage) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(`/api/config/encrypted/${endpoint}`, fieldValue, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSuccess(successMessage);
        setError(null);

        // Clear the editing state for this field
        setEditingEncryptedFields(prev => ({
          ...prev,
          [fieldName]: false
        }));
        setEncryptedFieldValues(prev => ({
          ...prev,
          [fieldName]: ''
        }));

        setTimeout(() => setSuccess(null), 3000);
        return true;
      }
    } catch (err) {
      console.error(`Error saving ${fieldName}:`, err);
      setError(`Failed to save ${fieldName}. Please try again.`);
      return false;
    }
  };

  // Specific save functions for each encrypted field
  const saveStripeSecret = async (secretKey) => {
    return await saveEncryptedField(
      'stripe-secret',
      { secretKey },
      'stripe.secretKey',
      'Stripe secret key updated successfully!'
    );
  };

  const saveTwilioAuthToken = async (authToken) => {
    return await saveEncryptedField(
      'twilio-auth-token',
      { authToken },
      'twilio.authToken',
      'Twilio auth token updated successfully!'
    );
  };

  const saveSmtpPassword = async (password) => {
    return await saveEncryptedField(
      'smtp-password',
      { password },
      'smtp.pass',
      'SMTP password updated successfully!'
    );
  };

  const saveOpenAiApiKey = async (apiKey) => {
    return await saveEncryptedField(
      'openai-api-key',
      { apiKey },
      'openai.apiKey',
      'OpenAI API key updated successfully!'
    );
  };

  const saveUberClientSecret = async (clientSecret) => {
    return await saveEncryptedField(
      'uber-client-secret',
      { clientSecret },
      'uber.clientSecret',
      'Uber client secret updated successfully!'
    );
  };

  const saveMetaPageAccessToken = async (pageAccessToken) => {
    return await saveEncryptedField(
      'meta-page-access-token',
      { pageAccessToken },
      'meta.pageAccessToken',
      'Meta page access token updated successfully!'
    );
  };

  const saveMetaAppSecret = async (appSecret) => {
    return await saveEncryptedField(
      'meta-app-secret',
      { appSecret },
      'meta.appSecret',
      'Meta app secret updated successfully!'
    );
  };

  const saveCanadaPostPassword = async (password) => {
    return await saveEncryptedField(
      'canada-post-password',
      { password },
      'canadaPost.password',
      'Canada Post password updated successfully!'
    );
  };

  const saveGoogleMapsApiKey = async (apiKey) => {
    return await saveEncryptedField(
      'google-maps-api-key',
      { apiKey },
      'apiKeys.googleMaps',
      'Google Maps API key updated successfully!'
    );
  };

  const saveZapierWebhookSecret = async (webhookSecret) => {
    return await saveEncryptedField(
      'zapier-webhook-secret',
      { webhookSecret },
      'zapier.webhookSecret',
      'Zapier webhook secret updated successfully!'
    );
  };

  // Configuration state
  const [config, setConfig] = useState({
    // General Settings
    siteName: "",
    siteTagline: "",
    siteDescription: "",
    siteKeywords: "",
    logoUrl: "",
    faviconUrl: "",

    // Pricing information
    onlyPricingTier: false,
    businessOrderCount: "",
    businessUserCharging: false,
    businessUserChargingAmount: "",
    businessWholesalePricing: false,
    showOrderSuccessPopup: false,
    orderSuccessPopupContent: "",
    uberDeliveryForItemsWithRegularPrice: false,

    // Contact Information
    contactEmail: "",
    contactPhone: "",
    contactAddress: "",

    // Social Media
    socialLinks: {
      facebook: "",
      twitter: "",
      instagram: "",
      linkedin: "",
      youtube: "",
    },

    // Colors & Branding
    colors: {
      primary: "#3B82F6",
      secondary: "#10B981",
      accent: "#8B5CF6",
      inputBorder: "#eae7e1",
      textPrimary: "#111827",
      textSecondary: "#6B7280",
      background: "#FFFFFF",
      backgroundAlt: "#F3F4F6",
    },

    // Typography
    typography: {
      headingFont: "Inter",
      bodyFont: "Inter",
      baseSize: "16px",
    },

    // SEO & Meta
    metaTitle: "",
    metaDescription: "",
    googleAnalyticsId: "",

    // Features Toggles
    features: {
      enableBlog: true,
      enableNewsletter: true,
      enableSearch: true,
      enableDarkMode: false,
    },

    // API Keys & Integrations
    apiKeys: {
      googleMaps: "",
      mailchimp: "",
    },

    zapier: {
      webhookSecret: "",
    },
    company: {
      name: "",
      phone: "",
      email: "",
      addressLine1: "",
      city: "",
      province: "",
      postalCode: "",
    },
    environment: {
      nodeEnv: "production",
      timezone: "America/Vancouver",
      frontendUrl: "",
      clientUrl: "",
    },
    store: {
      address: "",
      city: "",
      state: "",
      postalCode: "",
    },

    // Analytics
    analytics: {
      gtagId: '',
      gaId: '',
      googleAnalyticsId: '',
      conversionId: '',
      conversionLabel: ''
    },

  });

  // Check admin status
  useEffect(() => {
    if (!user || !user.isAdmin) {
      setError("You do not have permission to access this section");
      setLoading(false);
    }
  }, [user]);

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.get("/api/config", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        // Set the config with placeholders for encrypted fields
        setConfig((prevConfig) => ({
          ...prevConfig,
          ...response.data,
        }));

        // Clear any existing encrypted field editing state
        setEditingEncryptedFields({});
        setEncryptedFieldValues({});

        console.log('Config loaded successfully with encrypted field placeholders');
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching configuration:", err);
      setError("Failed to load configuration. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Add this function to handle encrypted field editing
  const toggleEncryptedField = (fieldName) => {
    setEditingEncryptedFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const handleEncryptedFieldChange = (fieldName, value) => {
    setEncryptedFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };


  // Improved EncryptedField component in ConfigManagement.js
  const EncryptedField = ({ label, fieldName, placeholder, saveFunction }) => {
    const isEditing = editingEncryptedFields[fieldName];
    const currentValue = encryptedFieldValues[fieldName] || '';
    const [saving, setSaving] = useState(false);

    const handleEdit = () => {
      setEditingEncryptedFields(prev => ({
        ...prev,
        [fieldName]: true
      }));
    };

    const handleCancel = () => {
      setEditingEncryptedFields(prev => ({
        ...prev,
        [fieldName]: false
      }));
      // Clear the value when canceling
      setEncryptedFieldValues(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    };

    const handleSave = async () => {
      if (!currentValue || currentValue.trim() === '') {
        setError('Please enter a value');
        return;
      }

      setSaving(true);
      const success = await saveFunction(currentValue);
      setSaving(false);

      // The saveFunction handles the rest (clearing editing state, showing success message)
    };

    const handleChange = (e) => {
      setEncryptedFieldValues(prev => ({
        ...prev,
        [fieldName]: e.target.value
      }));
    };

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        {!isEditing ? (
          <div className="flex items-center">
            <div className="flex-1 p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
              {currentValue && currentValue !== '' ? '••••••••••••••••' : '••••••••••••••••'}
            </div>
            <button
              type="button"
              onClick={handleEdit}
              className="ml-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="password"
                value={currentValue}
                onChange={handleChange}
                placeholder={placeholder}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={!currentValue || currentValue.trim() === '' || saving}
                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm flex items-center"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Enter new value to update this encrypted field.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Field change handlers that avoid unnecessary re-renders
  const handleFieldChange = useCallback((fieldName, value) => {
    setConfig((prev) => {
      if (fieldName.includes(".")) {
        const [section, property] = fieldName.split(".");
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [property]: value,
          },
        };
      } else {
        return {
          ...prev,
          [fieldName]: value,
        };
      }
    });
  }, []);

  // Specialized handler for color changes
  const handleColorChange = useCallback((colorName, value) => {
    setConfig((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorName]: value,
      },
    }));
  }, []);

  // Save configuration

  // Fixed saveConfig function in ConfigManagement.js
  const saveConfig = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      const token = localStorage.getItem("token");

      // Create the config to save - start with all regular fields
      const configToSave = { ...config };

      // Handle encrypted field updates properly
      Object.keys(encryptedFieldValues).forEach(fieldName => {
        const newValue = encryptedFieldValues[fieldName];

        // Only include fields that have actual new values (not empty/placeholder)
        if (newValue &&
          newValue.trim() !== '' &&
          newValue !== '***encrypted***' &&
          !newValue.startsWith('***')) {

          const [section, field] = fieldName.split('.');

          // Ensure the section exists in configToSave
          if (!configToSave[section]) {
            configToSave[section] = {};
          }

          // Set the new value
          configToSave[section][field] = newValue;
          console.log(`Frontend: Including encrypted field update: ${fieldName}`);
        } else {
          console.log(`Frontend: Skipping encrypted field (no change): ${fieldName}`);
        }
      });

      // Remove placeholder values for encrypted fields that weren't changed
      const encryptedFields = [
        'stripe.secretKey',
        'twilio.authToken',
        'smtp.pass',
        'openai.apiKey',
        'uber.clientSecret',
        'meta.pageAccessToken',
        'meta.appSecret',
        'canadaPost.password',
        'apiKeys.googleMaps',
        'zapier.webhookSecret'
      ];

      // For encrypted fields not being updated, remove placeholders to avoid re-encryption
      encryptedFields.forEach(fieldPath => {
        const [section, field] = fieldPath.split('.');

        // If this field wasn't edited, don't send it to avoid re-encryption
        if (!encryptedFieldValues[fieldPath] ||
          encryptedFieldValues[fieldPath].trim() === '' ||
          encryptedFieldValues[fieldPath] === '***encrypted***') {

          if (configToSave[section] && configToSave[section][field] === '***encrypted***') {
            // Remove the placeholder so backend knows not to process this field
            delete configToSave[section][field];
            console.log(`Frontend: Removed placeholder for: ${fieldPath}`);
          }
        }
      });

      console.log('Frontend: Sending config update:', {
        regularFields: Object.keys(configToSave).filter(key =>
          !['stripe', 'twilio', 'smtp', 'openai', 'uber', 'meta', 'canadaPost', 'apiKeys', 'zapier'].includes(key)
        ),
        encryptedUpdates: Object.keys(encryptedFieldValues).filter(key =>
          encryptedFieldValues[key] && encryptedFieldValues[key].trim() !== ''
        )
      });

      const response = await axios.post("/api/config", configToSave, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setSuccess("Configuration saved successfully!");
        setError(null);

        // Clear edited encrypted fields after successful save
        setEncryptedFieldValues({});
        setEditingEncryptedFields({});

        // Update the config state with the response (which has placeholders for encrypted fields)
        setConfig(response.data);

        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error("Error saving configuration:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSection((prevExpanded) =>
      prevExpanded === section ? null : section
    );
  }, []);

  // Clear notification messages
  const clearNotifications = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-2xl font-semibold">Website Configuration</h2>

      <div className="bg-gray-50 p-6 rounded-2xl">
        {/* Notification area */}
        <div className="container mx-auto px-4 py-2">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex justify-between items-center">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <p>{error}</p>
              </div>
              <button
                onClick={clearNotifications}
                className="text-red-500 hover:text-red-700"
                type="button"
              >
                ×
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex justify-between items-center">
              <div className="flex items-center">
                <Check className="w-5 h-5 mr-2" />
                <p>{success}</p>
              </div>
              <button
                onClick={clearNotifications}
                className="text-green-500 hover:text-green-700"
                type="button"
              >
                ×
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="h-64 flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={saveConfig} className="space-y-4">
            {/* Form header */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                <h1 className="text-2xl font-semibold text-gray-800">
                  Website Configuration
                </h1>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save All Settings
                    </>
                  )}
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Manage your website's settings, appearance, and behavior.
                Changes will be applied site-wide.
              </p>
            </div>

            {/* Configuration Sections */}
            <div className="grid grid-cols-1 gap-6">
              {/* General Settings */}
              <ConfigSection
                title="General Settings"
                icon={Globe}
                id="general"
                isExpanded={expandedSection === "general"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Website Name"
                      name="siteName"
                      value={config.siteName}
                      onChange={handleFieldChange}
                      required={true}
                      helperText="This will appear in the browser tab and across the site"
                    />

                    <TextField
                      label="Tagline"
                      name="siteTagline"
                      value={config.siteTagline}
                      onChange={handleFieldChange}
                      helperText="A short phrase describing your website"
                    />

                    <TextArea
                      label="Site Description"
                      name="siteDescription"
                      value={config.siteDescription}
                      onChange={handleFieldChange}
                      helperText="A longer description of your website (used for SEO)"
                    />
                  </div>

                  <div className="space-y-4">
                    <TextField
                      label="Site Keywords"
                      name="siteKeywords"
                      value={config.siteKeywords}
                      onChange={handleFieldChange}
                      helperText="Comma-separated keywords for SEO"
                    />

                    <FileUploadField
                      label="Logo"
                      name="logoUrl"
                      value={config.logoUrl}
                      onChange={handleFieldChange}
                      accept="image/*"
                      helperText="Upload your logo image (recommended: 200x200px, PNG/JPG)"
                    />

                    <FileUploadField
                      label="Favicon"
                      name="faviconUrl"
                      value={config.faviconUrl}
                      onChange={handleFieldChange}
                      accept="image/x-icon,image/png"
                      helperText="Upload your favicon (recommended: 32x32px, ICO or PNG)"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Company Information */}
              <ConfigSection
                title="Company Information"
                icon={Mail}
                id="companyInfo"
                isExpanded={expandedSection === "companyInfo"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Company Name"
                      name="company.name"
                      value={config.company?.name}
                      onChange={handleFieldChange}
                    />
                    <TextField
                      label="Company Phone"
                      name="company.phone"
                      value={config.company?.phone}
                      onChange={handleFieldChange}
                    />
                    <TextField
                      label="Company Email"
                      name="company.email"
                      value={config.company?.email}
                      onChange={handleFieldChange}
                      type="email"
                    />
                  </div>
                  <div className="space-y-4">
                    <TextField
                      label="Address Line 1"
                      name="company.addressLine1"
                      value={config.company?.addressLine1}
                      onChange={handleFieldChange}
                    />
                    <TextField
                      label="City"
                      name="company.city"
                      value={config.company?.city}
                      onChange={handleFieldChange}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <TextField
                        label="Province"
                        name="company.province"
                        value={config.company?.province}
                        onChange={handleFieldChange}
                      />
                      <TextField
                        label="Postal Code"
                        name="company.postalCode"
                        value={config.company?.postalCode}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                </div>
              </ConfigSection>

              {/* Environment Settings */}
              <ConfigSection
                title="Environment Settings"
                icon={Globe}
                id="environmentSettings"
                isExpanded={expandedSection === "environmentSettings"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <SelectField
                      label="Environment"
                      name="environment.nodeEnv"
                      value={config.environment?.nodeEnv}
                      onChange={handleFieldChange}
                      options={[
                        { value: "development", label: "Development" },
                        { value: "staging", label: "Staging" },
                        { value: "production", label: "Production" }
                      ]}
                    />
                    <SelectField
                      label="Timezone"
                      name="environment.timezone"
                      value={config.environment?.timezone}
                      onChange={handleFieldChange}
                      options={[
                        { value: "America/Vancouver", label: "America/Vancouver (PST)" },
                        { value: "America/Toronto", label: "America/Toronto (EST)" },
                        { value: "America/New_York", label: "America/New_York (EST)" },
                        { value: "UTC", label: "UTC" }
                      ]}
                    />
                  </div>
                  <div className="space-y-4">
                    <TextField
                      label="Frontend URL"
                      name="environment.frontendUrl"
                      value={config.environment?.frontendUrl}
                      onChange={handleFieldChange}
                      helperText="Main website URL"
                    />
                    <TextField
                      label="Client URL"
                      name="environment.clientUrl"
                      value={config.environment?.clientUrl}
                      onChange={handleFieldChange}
                      helperText="Client application URL"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Store Location */}
              <ConfigSection
                title="Store Location (Uber Delivery)"
                icon={MapPin}
                id="storeLocation"
                isExpanded={expandedSection === "storeLocation"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Store Address"
                      name="store.address"
                      value={config.store?.address}
                      onChange={handleFieldChange}
                    />
                    <TextField
                      label="City"
                      name="store.city"
                      value={config.store?.city}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <div className="space-y-4">
                    <TextField
                      label="State/Province"
                      name="store.state"
                      value={config.store?.state}
                      onChange={handleFieldChange}
                    />
                    <TextField
                      label="Postal Code"
                      name="store.postalCode"
                      value={config.store?.postalCode}
                      onChange={handleFieldChange}
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Pricing Information */}
              <ConfigSection
                title="Cart & Pricing Information"
                icon={Money}
                id="pricing"
                isExpanded={expandedSection === "pricing"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="mb-4">
                    <Checkbox
                      label="Only Show Pricing Tiers In Users' Cart"
                      name="onlyPricingTier"
                      checked={config.onlyPricingTier}
                      onChange={handleFieldChange}
                      helperText="By Selecting, Only pricing tiers will be shown"
                    />
                  </div>

                  <div className="mb-4">
                    <TextField
                      label="Business Order Count"
                      name="businessOrderCount"
                      value={config.businessOrderCount}
                      onChange={handleFieldChange}
                      type="number"
                    />
                  </div>

                  <div className="mb-4">
                    <Checkbox
                      label="Charge Business Users for Account Verification"
                      name="businessUserCharging"
                      checked={config.businessUserCharging}
                      onChange={handleFieldChange}
                      helperText="If enabled, business users will be charged for account verification (refundable)"
                    />
                  </div>

                  {config.businessUserCharging && (
                    <div className="mb-4">
                      <CurrencyField
                        label="Business Verification Amount"
                        name="businessUserChargingAmount"
                        value={config.businessUserChargingAmount}
                        onChange={handleFieldChange}
                        helperText="This amount will be charged to business users for verification (refundable)"
                      />
                    </div>
                  )}

                  <div className="mb-4">
                    <Checkbox
                      label="Enable Wholesale Pricing for Business Accounts"
                      name="businessWholesalePricing"
                      checked={config.businessWholesalePricing}
                      onChange={handleFieldChange}
                      helperText="If enabled, business users will see wholesale prices in cart regardless of quantity"
                    />
                  </div>

                  <div className="mb-4">
                    <Checkbox
                      label="Enable Uber Delivery for Items with Regular Price"
                      name="uberDeliveryForItemsWithRegularPrice"
                      checked={config.uberDeliveryForItemsWithRegularPrice}
                      onChange={handleFieldChange}
                      helperText="If enabled, items with regular price in the cart will be deliverd by Uber if delivery is selected"
                    />
                  </div>

                  <div className="mb-4">
                    <Checkbox
                      label="Show Custom Popup After Order Placement"
                      name="showOrderSuccessPopup"
                      checked={config.showOrderSuccessPopup}
                      onChange={handleFieldChange}
                      helperText="Display a custom message popup after successful order placement"
                    />
                  </div>

                  {config.showOrderSuccessPopup && (
                    <div className="mb-4 ml-6">
                      <TextArea
                        label="Order Success Popup Content"
                        name="orderSuccessPopupContent"
                        value={config.orderSuccessPopupContent}
                        onChange={handleFieldChange}
                        rows={6}
                        helperText="This content will be displayed in the popup after order placement. You can use HTML for formatting."
                        placeholder="Thank you for your order! We'll process it shortly..."
                      />
                    </div>
                  )}
                </div>



              </ConfigSection>

              {/* Contact Information */}
              <ConfigSection
                title="Contact Information"
                icon={Mail}
                id="contact"
                isExpanded={expandedSection === "contact"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <IconTextField
                      label="Contact Email"
                      name="contactEmail"
                      value={config.contactEmail}
                      onChange={handleFieldChange}
                      icon={Mail}
                      type="email"
                    />

                    <IconTextField
                      label="Contact Phone"
                      name="contactPhone"
                      value={config.contactPhone}
                      onChange={handleFieldChange}
                      icon={Phone}
                    />
                  </div>

                  <div className="space-y-4">
                    <TextArea
                      label="Contact Address"
                      name="contactAddress"
                      value={config.contactAddress}
                      onChange={handleFieldChange}
                      icon={MapPin}
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Social Media */}
              <ConfigSection
                title="Social Media"
                icon={Facebook}
                id="social"
                isExpanded={expandedSection === "social"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <IconTextField
                      label="Facebook"
                      name="socialLinks.facebook"
                      value={config.socialLinks?.facebook}
                      onChange={handleFieldChange}
                      icon={Facebook}
                      placeholder="https://facebook.com/yourpage"
                    />

                    <IconTextField
                      label="Twitter"
                      name="socialLinks.twitter"
                      value={config.socialLinks?.twitter}
                      onChange={handleFieldChange}
                      icon={Twitter}
                      placeholder="https://twitter.com/yourhandle"
                    />

                    <IconTextField
                      label="Instagram"
                      name="socialLinks.instagram"
                      value={config.socialLinks?.instagram}
                      onChange={handleFieldChange}
                      icon={Instagram}
                      placeholder="https://instagram.com/yourhandle"
                    />
                  </div>

                  <div className="space-y-4">
                    <IconTextField
                      label="LinkedIn"
                      name="socialLinks.linkedin"
                      value={config.socialLinks?.linkedin}
                      onChange={handleFieldChange}
                      icon={Linkedin}
                      placeholder="https://linkedin.com/company/yourcompany"
                    />

                    <IconTextField
                      label="YouTube"
                      name="socialLinks.youtube"
                      value={config.socialLinks?.youtube}
                      onChange={handleFieldChange}
                      icon={Youtube}
                      placeholder="https://youtube.com/c/yourchannel"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Colors & Branding */}
              <ConfigSection
                title="Colors & Branding"
                icon={Palette}
                id="colors"
                isExpanded={expandedSection === "colors"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Brand Colors</h4>

                    <ColorField
                      label="Primary"
                      colorName="primary"
                      color={config.colors?.primary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Secondary"
                      colorName="secondary"
                      color={config.colors?.secondary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Accent"
                      colorName="accent"
                      color={config.colors?.accent}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Border"
                      colorName="inputBorder"
                      color={config.colors?.inputBorder}
                      onChange={handleColorChange}
                    />
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">
                      Text & Background Colors
                    </h4>

                    <ColorField
                      label="Text Primary"
                      colorName="textPrimary"
                      color={config.colors?.textPrimary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Text Secondary"
                      colorName="textSecondary"
                      color={config.colors?.textSecondary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Background"
                      colorName="background"
                      color={config.colors?.background}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Background Alt"
                      colorName="backgroundAlt"
                      color={config.colors?.backgroundAlt}
                      onChange={handleColorChange}
                    />
                  </div>
                </div>

                {/* Preview section for colors */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Color Preview
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.primary }}
                    >
                      Primary
                    </div>
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.secondary }}
                    >
                      Secondary
                    </div>
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.accent }}
                    >
                      Accent
                    </div>
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.inputBorder }}
                    >
                      Border
                    </div>
                    <div
                      className="p-4 rounded-md flex items-center justify-center h-20"
                      style={{
                        backgroundColor: config.colors?.background,
                        color: config.colors?.textPrimary,
                      }}
                    >
                      Background / Text
                    </div>
                    <div
                      className="p-4 rounded-md flex items-center justify-center h-20"
                      style={{
                        backgroundColor: config.colors?.backgroundAlt,
                        color: config.colors?.textSecondary,
                      }}
                    >
                      Background Alt / Text Secondary
                    </div>
                  </div>
                </div>
              </ConfigSection>

              {/* Typography */}
              <ConfigSection
                title="Typography"
                icon={Type}
                id="typography"
                isExpanded={expandedSection === "typography"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <SelectField
                      label="Heading Font"
                      name="typography.headingFont"
                      value={config.typography?.headingFont}
                      onChange={handleFieldChange}
                      options={[
                        { value: "Inter", label: "Inter" },
                        { value: "Roboto", label: "Roboto" },
                        { value: "Open Sans", label: "Open Sans" },
                        { value: "Montserrat", label: "Montserrat" },
                        { value: "Lato", label: "Lato" },
                        { value: "Poppins", label: "Poppins" },
                        {
                          value: "Playfair Display",
                          label: "Playfair Display",
                        },
                        { value: "Merriweather", label: "Merriweather" },
                      ]}
                    />

                    <SelectField
                      label="Body Font"
                      name="typography.bodyFont"
                      value={config.typography?.bodyFont}
                      onChange={handleFieldChange}
                      options={[
                        { value: "Inter", label: "Inter" },
                        { value: "Roboto", label: "Roboto" },
                        { value: "Open Sans", label: "Open Sans" },
                        { value: "Montserrat", label: "Montserrat" },
                        { value: "Lato", label: "Lato" },
                        { value: "Poppins", label: "Poppins" },
                        { value: "Source Sans Pro", label: "Source Sans Pro" },
                        { value: "Nunito", label: "Nunito" },
                      ]}
                    />
                  </div>

                  <div className="space-y-4">
                    <SelectField
                      label="Base Font Size"
                      name="typography.baseSize"
                      value={config.typography?.baseSize}
                      onChange={handleFieldChange}
                      options={[
                        { value: "14px", label: "Small (14px)" },
                        { value: "16px", label: "Medium (16px)" },
                        { value: "18px", label: "Large (18px)" },
                      ]}
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Typography Preview
                  </h4>
                  <div
                    className="p-6 border border-gray-200 rounded-md"
                    style={{
                      fontFamily: `${config.typography?.bodyFont}, system-ui, sans-serif`,
                      fontSize: config.typography?.baseSize,
                    }}
                  >
                    <h1
                      className="text-3xl mb-3"
                      style={{
                        fontFamily: `${config.typography?.headingFont}, system-ui, sans-serif`,
                        color: config.colors?.textPrimary,
                      }}
                    >
                      Heading Font: {config.typography?.headingFont}
                    </h1>
                    <p
                      className="mb-4"
                      style={{ color: config.colors?.textPrimary }}
                    >
                      Body Font: {config.typography?.bodyFont} at{" "}
                      {config.typography?.baseSize} size. This is a preview of
                      how text will appear on your website using your selected
                      typography settings.
                    </p>
                    <p style={{ color: config.colors?.textSecondary }}>
                      This is secondary text using your selected colors and
                      fonts.
                    </p>
                  </div>
                </div>
              </ConfigSection>

              {/* SEO & Meta */}
              <ConfigSection
                title="SEO & Meta"
                icon={Info}
                id="seo"
                isExpanded={expandedSection === "seo"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Default Meta Title"
                      name="metaTitle"
                      value={config.metaTitle}
                      onChange={handleFieldChange}
                      helperText="Defaults to Site Name if empty"
                    />

                    <TextArea
                      label="Default Meta Description"
                      name="metaDescription"
                      value={config.metaDescription}
                      onChange={handleFieldChange}
                      helperText="Defaults to Site Description if empty"
                    />
                  </div>

                  <div className="space-y-4">
                    <TextField
                      label="Google Analytics ID"
                      name="googleAnalyticsId"
                      value={config.googleAnalyticsId}
                      onChange={handleFieldChange}
                      placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
                      helperText="Your Google Analytics tracking ID"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Feature Toggles */}
              <ConfigSection
                title="Feature Toggles"
                icon={Settings}
                id="features"
                isExpanded={expandedSection === "features"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Checkbox
                      label="Enable Blog"
                      name="features.enableBlog"
                      checked={config.features?.enableBlog}
                      onChange={handleFieldChange}
                      helperText="Show blog posts section on website"
                    />

                    <Checkbox
                      label="Enable Newsletter"
                      name="features.enableNewsletter"
                      checked={config.features?.enableNewsletter}
                      onChange={handleFieldChange}
                      helperText="Show newsletter signup forms"
                    />
                  </div>

                  <div>
                    <Checkbox
                      label="Enable Search"
                      name="features.enableSearch"
                      checked={config.features?.enableSearch}
                      onChange={handleFieldChange}
                      helperText="Show search bar in header"
                    />

                    <Checkbox
                      label="Enable Dark Mode"
                      name="features.enableDarkMode"
                      checked={config.features?.enableDarkMode}
                      onChange={handleFieldChange}
                      helperText="Allow users to toggle between light and dark themes"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* API Keys & Integrations */}
              <ConfigSection
                title="API Keys & Integrations"
                icon={Layout}
                id="apiKeys"
                isExpanded={expandedSection === "apiKeys"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <EncryptedField
                      label="Google Maps API Key"
                      fieldName="apiKeys.googleMaps"
                      placeholder="Your Google Maps API key"
                      saveFunction={saveGoogleMapsApiKey}
                    />
                  </div>

                  <div className="space-y-4">
                    <EncryptedField
                      label="Zapier Webhook Secret"
                      fieldName="zapier.webhookSecret"
                      placeholder="Your Zapier webhook secret"
                      saveFunction={saveZapierWebhookSecret}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <EncryptedField
                    label="OpenAI API Key"
                    fieldName="openai.apiKey"
                    placeholder="sk-..."
                    saveFunction={saveOpenAiApiKey}
                  />
                </div>

                <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 rounded-md">
                  <div className="flex">
                    <Info className="h-5 w-5 mr-2" />
                    <div>
                      <p className="text-sm font-medium">Security Notice</p>
                      <p className="text-xs mt-1">
                        API keys are stored encrypted and handled separately for security.
                        Click "Edit" to update any key individually.
                      </p>
                    </div>
                  </div>
                </div>
              </ConfigSection>

              {/* Payment Integration */}
              <ConfigSection
                title="Payment Integration"
                icon={Money}
                id="payment"
                isExpanded={expandedSection === "payment"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <TextField
                    label="Stripe Public Key"
                    name="stripe.publicKey"
                    value={config.stripe?.publicKey}
                    onChange={handleFieldChange}
                  />
                  <EncryptedField
                    label="Stripe Secret Key"
                    fieldName="stripe.secretKey"
                    placeholder="sk_test_..."
                    saveFunction={saveStripeSecret}
                  />
                </div>
              </ConfigSection>

              {/* Communication Services */}
              <ConfigSection
                title="Communication Services"
                icon={Phone}
                id="communication"
                isExpanded={expandedSection === "communication"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Twilio SMS</h4>
                    <TextField
                      label="Account SID"
                      name="twilio.accountSid"
                      value={config.twilio?.accountSid}
                      onChange={handleFieldChange}
                    />
                    <EncryptedField
                      label="Twilio Auth Token"
                      fieldName="twilio.authToken"
                      placeholder="Your Twilio auth token"
                      saveFunction={saveTwilioAuthToken}
                    />
                    <TextField
                      label="Phone Number"
                      name="twilio.phoneNumber"
                      value={config.twilio?.phoneNumber}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">SMTP Email</h4>
                    <TextField
                      label="SMTP Host"
                      name="smtp.host"
                      value={config.smtp?.host}
                      onChange={handleFieldChange}
                    />
                    <TextField
                      label="SMTP User"
                      name="smtp.user"
                      value={config.smtp?.user}
                      onChange={handleFieldChange}
                    />
                    <EncryptedField
                      label="SMTP Password"
                      fieldName="smtp.pass"
                      placeholder="Your SMTP password"
                      saveFunction={saveSmtpPassword}
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* External APIs */}
              {/* <ConfigSection
                title="External APIs"
                icon={Settings}
                id="external"
                isExpanded={expandedSection === "external"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <TextField
                    label="OpenAI API Key"
                    name="openai.apiKey"
                    value={config.openai?.apiKey ? "***encrypted***" : ""}
                    onChange={handleFieldChange}
                    type="password"
                  />
                </div>
              </ConfigSection> */}

              {/* Analytics & Tracking */}
              <ConfigSection
                title="Analytics & Tracking"
                icon={BarChart3}
                id="analytics"
                isExpanded={expandedSection === "analytics"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Google Tag Manager ID"
                      name="analytics.gtagId"
                      value={config.analytics?.gtagId}
                      onChange={handleFieldChange}
                      placeholder="GTM-XXXXXXX"
                      helperText="Your Google Tag Manager container ID"
                    />

                    <TextField
                      label="Google Analytics ID"
                      name="analytics.gaId"
                      value={config.analytics?.gaId}
                      onChange={handleFieldChange}
                      placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
                      helperText="Your Google Analytics tracking ID"
                    />
                  </div>

                  <div className="space-y-4">
                    <TextField
                      label="Conversion ID"
                      name="analytics.conversionId"
                      value={config.analytics?.conversionId}
                      onChange={handleFieldChange}
                      placeholder="AW-XXXXXXXXXX"
                      helperText="Google Ads conversion tracking ID"
                    />

                    <TextField
                      label="Conversion Label"
                      name="analytics.conversionLabel"
                      value={config.analytics?.conversionLabel}
                      onChange={handleFieldChange}
                      placeholder="XXXXXXXXXX/XXXXXXXXXX"
                      helperText="Google Ads conversion label"
                    />
                  </div>
                </div>

                <div className="mt-4 p-4 bg-blue-50 text-blue-800 rounded-md">
                  <div className="flex">
                    <Info className="h-5 w-5 mr-2" />
                    <div>
                      <p className="text-sm font-medium">Analytics Setup</p>
                      <p className="text-xs mt-1">
                        These settings will be used to generate the appropriate tracking scripts in your index.html file.
                      </p>
                    </div>
                  </div>
                </div>
              </ConfigSection>

            </div>

            {/* <button
              onClick={async () => {
                if (
                  window.confirm(
                    "Are you sure you want to migrate all images from MongoDB to the file system? This process cannot be undone and may take several minutes."
                  )
                ) {
                  try {
                    console.log({
                      type: "info",
                      message:
                        "Migration started. This may take several minutes...",
                    });

                    const response = await fetch("/api/admin/migrate-images", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem(
                          "token"
                        )}`,
                      },
                    });

                    const data = await response.json();

                    if (response.ok) {
                      console.log({
                        type: "success",
                        message:
                          "Image migration completed successfully! Please refresh the page to see changes.",
                      });
                    } else {
                      throw new Error(data.error || "Migration failed");
                    }
                  } catch (error) {
                    console.error("Migration error:", error);
                    console.log({
                      type: "error",
                      message: `Migration failed: ${error.message}`,
                    });
                  }
                }
              }}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-200"
            >
              Migrate Images from MongoDB to File System
            </button>

            <button
              onClick={async () => {
                if (
                  window.confirm(
                    "This will update your database to use the filesystem image paths. This operation is safe to run multiple times. Continue?"
                  )
                ) {
                  try {
                    console.log({
                      type: "info",
                      message:
                        "Database update started. This may take a few minutes...",
                    });

                    const response = await fetch(
                      "/api/admin/update-image-references",
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${localStorage.getItem(
                            "token"
                          )}`,
                        },
                      }
                    );

                    const data = await response.json();

                    if (response.ok) {
                      console.log({
                        type: "success",
                        message: `Database updated successfully! Products: ${data.stats.productsUpdated}, Categories: ${data.stats.categoriesUpdated}, Parent Categories: ${data.stats.parentCategoriesUpdated}`,
                      });
                    } else {
                      throw new Error(data.error || "Update failed");
                    }
                  } catch (error) {
                    console.error("Database update error:", error);
                    console.log({
                      type: "error",
                      message: `Update failed: ${error.message}`,
                    });
                  }
                }
              }}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-200"
            >
              Update Database Image References
            </button>
            <p>
              {" "}
              how it works:
              <br />
              first comment the new model in product, categry & parent category
              to include the img id in each model (using old implemenation).
              <br />
              then, press the migrate img from mongo to file system to do the
              trasfer. <br />
              last, comment the old img id filed in product model, parent and
              category to let system save new img data in each model & press
              update database
            </p> */}

            {/* Save Button */}
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving Configuration...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save All Settings
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConfigManagement;











// // encrypting everything
// // befoe haveing everythingin configdatabase
// // src/components/admin/ConfigManagement.js
// import {
//   AlertCircle,
//   BarChart3,
//   Check,
//   ChevronDown,
//   ChevronUp,
//   Facebook,
//   Globe,
//   Info,
//   Instagram,
//   Layout,
//   Linkedin,
//   Loader2,
//   Mail,
//   MapPin,
//   DollarSign as Money,
//   Palette,
//   Phone,
//   Save,
//   Settings,
//   Twitter,
//   Type,
//   Upload,
//   X,
//   Youtube
// } from "lucide-react";
// import { memo, useCallback, useEffect, useState } from "react";
// import { useAuth } from "../../context/AuthContext";
// import axios from "../../utils/axiosHandler";
// import ColorPicker from "./shared/ColorPicker";

// // Memoized individual form components to prevent unnecessary re-renders
// const TextField = memo(
//   ({
//     label,
//     name,
//     value,
//     onChange,
//     placeholder,
//     className,
//     type = "text",
//     required = false,
//     helperText = null,
//   }) => {
//     // Create local state to avoid re-renders in parent component
//     const [localValue, setLocalValue] = useState(value || "");

//     // Update local state when prop changes
//     useEffect(() => {
//       setLocalValue(value || "");
//     }, [value]);

//     const handleChange = (e) => {
//       const newValue = e.target.value;
//       setLocalValue(newValue);
//     };

//     const handleBlur = () => {
//       // Only update parent state when field loses focus
//       if (localValue !== value) {
//         onChange(name, localValue);
//       }
//     };

//     return (
//       <div className="mb-4">
//         <label
//           className="block text-sm font-medium text-gray-700 mb-1"
//           htmlFor={name}
//         >
//           {label} {required && "*"}
//         </label>
//         <input
//           id={name}
//           name={name}
//           type={type}
//           value={localValue}
//           onChange={handleChange}
//           onBlur={handleBlur}
//           placeholder={placeholder}
//           required={required}
//           className={`w-full p-2 border border-gray-300 rounded-md ${className || ""
//             }`}
//         />
//         {helperText && (
//           <p className="text-xs text-gray-500 mt-1">{helperText}</p>
//         )}
//       </div>
//     );
//   }
// );

// const IconTextField = memo(
//   ({
//     label,
//     name,
//     value,
//     onChange,
//     icon: Icon,
//     className,
//     placeholder,
//     required = false,
//     helperText = null,
//   }) => {
//     const [localValue, setLocalValue] = useState(value || "");

//     useEffect(() => {
//       setLocalValue(value || "");
//     }, [value]);

//     const handleChange = (e) => {
//       const newValue = e.target.value;
//       setLocalValue(newValue);
//     };

//     const handleBlur = () => {
//       if (localValue !== value) {
//         onChange(name, localValue);
//       }
//     };

//     return (
//       <div className="mb-4">
//         <label
//           className="block text-sm font-medium text-gray-700 mb-1"
//           htmlFor={name}
//         >
//           {label} {required && "*"}
//         </label>
//         <div className="relative">
//           <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//             <Icon className="h-5 w-5 text-gray-400" />
//           </div>
//           <input
//             id={name}
//             name={name}
//             type="text"
//             value={localValue}
//             onChange={handleChange}
//             onBlur={handleBlur}
//             placeholder={placeholder}
//             required={required}
//             className={`w-full p-2 pl-10 border border-gray-300 rounded-md ${className || ""
//               }`}
//           />
//         </div>
//         {helperText && (
//           <p className="text-xs text-gray-500 mt-1">{helperText}</p>
//         )}
//       </div>
//     );
//   }
// );

// const TextArea = memo(
//   ({
//     label,
//     name,
//     value,
//     onChange,
//     rows = 3,
//     className,
//     required = false,
//     helperText = null,
//     icon: Icon = null,
//   }) => {
//     const [localValue, setLocalValue] = useState(value || "");

//     useEffect(() => {
//       setLocalValue(value || "");
//     }, [value]);

//     const handleChange = (e) => {
//       const newValue = e.target.value;
//       setLocalValue(newValue);
//     };

//     const handleBlur = () => {
//       if (localValue !== value) {
//         onChange(name, localValue);
//       }
//     };

//     return (
//       <div className="mb-4">
//         <label
//           className="block text-sm font-medium text-gray-700 mb-1"
//           htmlFor={name}
//         >
//           {label} {required && "*"}
//         </label>
//         <div className={Icon ? "relative" : ""}>
//           {Icon && (
//             <div className="absolute top-3 left-3 pointer-events-none">
//               <Icon className="h-5 w-5 text-gray-400" />
//             </div>
//           )}
//           <textarea
//             id={name}
//             name={name}
//             value={localValue}
//             onChange={handleChange}
//             onBlur={handleBlur}
//             rows={rows}
//             required={required}
//             className={`w-full p-2 ${Icon ? "pl-10" : ""
//               } border border-gray-300 rounded-md ${className || ""}`}
//           ></textarea>
//         </div>
//         {helperText && (
//           <p className="text-xs text-gray-500 mt-1">{helperText}</p>
//         )}
//       </div>
//     );
//   }
// );

// const SelectField = memo(
//   ({
//     label,
//     name,
//     value,
//     onChange,
//     options,
//     className,
//     required = false,
//     helperText = null,
//   }) => {
//     const [localValue, setLocalValue] = useState(value || "");

//     useEffect(() => {
//       setLocalValue(value || "");
//     }, [value]);

//     const handleChange = (e) => {
//       const newValue = e.target.value;
//       setLocalValue(newValue);
//       // For select fields, we can trigger onChange immediately since there's no typing
//       onChange(name, newValue);
//     };

//     return (
//       <div className="mb-4">
//         <label
//           className="block text-sm font-medium text-gray-700 mb-1"
//           htmlFor={name}
//         >
//           {label} {required && "*"}
//         </label>
//         <select
//           id={name}
//           name={name}
//           value={localValue}
//           onChange={handleChange}
//           required={required}
//           className={`w-full p-2 border border-gray-300 rounded-md ${className || ""
//             }`}
//         >
//           {options.map((option) => (
//             <option key={option.value} value={option.value}>
//               {option.label}
//             </option>
//           ))}
//         </select>
//         {helperText && (
//           <p className="text-xs text-gray-500 mt-1">{helperText}</p>
//         )}
//       </div>
//     );
//   }
// );

// const Checkbox = memo(
//   ({ label, name, checked, onChange, helperText = null }) => {
//     const handleChange = (e) => {
//       onChange(name, e.target.checked);
//     };

//     return (
//       <div className="mb-4">
//         <label className="flex items-center">
//           <input
//             type="checkbox"
//             name={name}
//             checked={checked || false}
//             onChange={handleChange}
//             className="h-4 w-4 text-blue-600 border-gray-300 rounded"
//           />
//           <span className="ml-2 text-sm font-medium text-gray-700">
//             {label}
//           </span>
//         </label>
//         {helperText && (
//           <p className="text-xs text-gray-500 mt-1 ml-6">{helperText}</p>
//         )}
//       </div>
//     );
//   }
// );

// const CurrencyField = memo(
//   ({
//     label,
//     name,
//     value,
//     onChange,
//     min = "0.01",
//     step = "0.01",
//     className,
//     required = false,
//     helperText = null,
//   }) => {
//     const [localValue, setLocalValue] = useState(value || "");

//     useEffect(() => {
//       setLocalValue(value || "");
//     }, [value]);

//     const handleChange = (e) => {
//       const newValue = e.target.value;
//       setLocalValue(newValue);
//     };

//     const handleBlur = () => {
//       if (localValue !== value) {
//         onChange(name, localValue);
//       }
//     };

//     return (
//       <div className="mb-4">
//         <label
//           className="block text-sm font-medium text-gray-700 mb-1"
//           htmlFor={name}
//         >
//           {label} {required && "*"}
//         </label>
//         <div className="relative">
//           <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//             <span className="text-gray-500">$</span>
//           </div>
//           <input
//             id={name}
//             name={name}
//             type="number"
//             min={min}
//             step={step}
//             value={localValue}
//             onChange={handleChange}
//             onBlur={handleBlur}
//             required={required}
//             className={`w-full p-2 pl-8 border border-gray-300 rounded-md ${className || ""
//               }`}
//           />
//         </div>
//         {helperText && (
//           <p className="text-xs text-gray-500 mt-1">{helperText}</p>
//         )}
//       </div>
//     );
//   }
// );

// const ColorField = memo(({ label, colorName, color, onChange }) => {
//   // Handle color change and pass it to parent
//   const handleColorChange = useCallback(
//     (newColor) => {
//       onChange(colorName, newColor);
//     },
//     [onChange, colorName]
//   );

//   return (
//     <div className="flex items-center mb-4">
//       <label className="block text-sm font-medium text-gray-700 w-32">
//         {label}
//       </label>
//       <div className="flex-grow">
//         <ColorPicker
//           color={color || "#ffffff"}
//           onChange={handleColorChange}
//           allowAlpha={false}
//         />
//       </div>
//     </div>
//   );
// });

// // Configuration section component
// const ConfigSection = memo(
//   ({ title, icon: Icon, id, isExpanded, onToggle, children }) => (
//     <div className="mb-6 bg-white rounded-lg shadow-sm overflow-hidden">
//       <button
//         onClick={() => onToggle(id)}
//         className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 text-left"
//         type="button"
//       >
//         <div className="flex items-center">
//           {Icon && <Icon className="w-5 h-5 text-blue-600 mr-2" />}
//           <h3 className="text-lg font-medium text-gray-900">{title}</h3>
//         </div>
//         {isExpanded ? (
//           <ChevronUp className="w-5 h-5 text-gray-500" />
//         ) : (
//           <ChevronDown className="w-5 h-5 text-gray-500" />
//         )}
//       </button>

//       {isExpanded && (
//         <div className="p-4 border-t border-gray-200">{children}</div>
//       )}
//     </div>
//   )
// );

// // File Upload Component
// const FileUploadField = memo(({
//   label,
//   name,
//   value,
//   onChange,
//   accept = "image/*",
//   helperText = null,
//   required = false
// }) => {
//   const [uploading, setUploading] = useState(false);
//   const [preview, setPreview] = useState(value);

//   // Update preview when value changes
//   useEffect(() => {
//     setPreview(value);
//   }, [value]);

//   const handleFileChange = async (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     setUploading(true);

//     try {
//       const formData = new FormData();
//       formData.append('file', file);

//       // Determine file type based on the field name
//       let fileType = '';
//       if (name === 'logoUrl') {
//         fileType = 'logo';
//       } else if (name === 'faviconUrl') {
//         fileType = 'favicon';
//       }

//       const token = localStorage.getItem('token');
//       const response = await axios.post(`/api/config/upload?fileType=${fileType}`, formData, {
//         headers: {
//           'Authorization': `Bearer ${token}`,
//           'Content-Type': 'multipart/form-data'
//         }
//       });

//       if (response.data.success) {
//         const fileUrl = response.data.url;
//         console.log('Upload successful:', fileUrl);
//         setPreview(fileUrl);
//         onChange(name, fileUrl);
//       } else {
//         throw new Error('Upload failed');
//       }
//     } catch (error) {
//       console.error('Upload error:', error);
//       alert('Failed to upload file. Please try again.');
//     } finally {
//       setUploading(false);
//     }
//   };

//   const removeFile = () => {
//     setPreview('');
//     onChange(name, '');
//   };

//   return (
//     <div className="mb-4">
//       <label className="block text-sm font-medium text-gray-700 mb-1">
//         {label} {required && "*"}
//       </label>

//       {preview ? (
//         <div className="relative">
//           <img
//             src={preview}
//             alt="Preview"
//             className="w-32 h-32 object-cover rounded-md border"
//           />
//           <button
//             type="button"
//             onClick={removeFile}
//             className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
//           >
//             <X className="w-4 h-4" />
//           </button>
//         </div>
//       ) : (
//         <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
//           <Upload className="mx-auto h-12 w-12 text-gray-400" />
//           <div className="mt-2">
//             <label htmlFor={`file-${name}`} className="cursor-pointer">
//               <span className="text-blue-600 hover:text-blue-500">
//                 {uploading ? 'Uploading...' : 'Click to upload'}
//               </span>
//               <input
//                 id={`file-${name}`}
//                 type="file"
//                 accept={accept}
//                 onChange={handleFileChange}
//                 className="hidden"
//                 disabled={uploading}
//               />
//             </label>
//           </div>
//           <p className="text-xs text-gray-500 mt-1">
//             {accept === "image/*" ? "PNG, JPG, GIF, WebP, ICO up to 5MB" : "File upload"}
//           </p>
//         </div>
//       )}

//       {helperText && (
//         <p className="text-xs text-gray-500 mt-1">{helperText}</p>
//       )}
//     </div>
//   );
// });

// // Main component
// const ConfigManagement = () => {
//   const { user } = useAuth();
//   const [loading, setLoading] = useState(true);
//   const [saving, setSaving] = useState(false);
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);
//   const [expandedSection, setExpandedSection] = useState("general");


//   const [editingEncryptedFields, setEditingEncryptedFields] = useState({});
//   const [encryptedFieldValues, setEncryptedFieldValues] = useState({});


//   // Configuration state
//   const [config, setConfig] = useState({
//     // General Settings
//     siteName: "",
//     siteTagline: "",
//     siteDescription: "",
//     siteKeywords: "",
//     logoUrl: "",
//     faviconUrl: "",

//     // Pricing information
//     onlyPricingTier: false,
//     businessOrderCount: "",
//     businessUserCharging: false,
//     businessUserChargingAmount: "",
//     businessWholesalePricing: false,
//     showOrderSuccessPopup: false,
//     orderSuccessPopupContent: "",
//     uberDeliveryForItemsWithRegularPrice: false,

//     // Contact Information
//     contactEmail: "",
//     contactPhone: "",
//     contactAddress: "",

//     // Social Media
//     socialLinks: {
//       facebook: "",
//       twitter: "",
//       instagram: "",
//       linkedin: "",
//       youtube: "",
//     },

//     // Colors & Branding
//     colors: {
//       primary: "#3B82F6",
//       secondary: "#10B981",
//       accent: "#8B5CF6",
//       inputBorder: "#eae7e1",
//       textPrimary: "#111827",
//       textSecondary: "#6B7280",
//       background: "#FFFFFF",
//       backgroundAlt: "#F3F4F6",
//     },

//     // Typography
//     typography: {
//       headingFont: "Inter",
//       bodyFont: "Inter",
//       baseSize: "16px",
//     },

//     // SEO & Meta
//     metaTitle: "",
//     metaDescription: "",
//     googleAnalyticsId: "",

//     // Features Toggles
//     features: {
//       enableBlog: true,
//       enableNewsletter: true,
//       enableSearch: true,
//       enableDarkMode: false,
//     },

//     // API Keys & Integrations
//     apiKeys: {
//       googleMaps: "",
//       mailchimp: "",
//     },

//     zapier: {
//       webhookSecret: "",
//     },
//     company: {
//       name: "",
//       phone: "",
//       email: "",
//       addressLine1: "",
//       city: "",
//       province: "",
//       postalCode: "",
//     },
//     environment: {
//       nodeEnv: "production",
//       timezone: "America/Vancouver",
//       frontendUrl: "",
//       clientUrl: "",
//     },
//     store: {
//       address: "",
//       city: "",
//       state: "",
//       postalCode: "",
//     },

//     // Analytics
//     analytics: {
//       gtagId: '',
//       gaId: '',
//       googleAnalyticsId: '',
//       conversionId: '',
//       conversionLabel: ''
//     },

//   });

//   // Check admin status
//   useEffect(() => {
//     if (!user || !user.isAdmin) {
//       setError("You do not have permission to access this section");
//       setLoading(false);
//     }
//   }, [user]);

//   // Fetch configuration
//   const fetchConfig = useCallback(async () => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem("token");


//       console.log(token)

//       const response = await axios.get("/api/config", {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });


//       if (response.data) {
//         setConfig((prevConfig) => ({
//           ...prevConfig,
//           ...response.data,
//         }));
//       }

//       setError(null);
//     } catch (err) {
//       console.error("Error fetching configuration:", err);
//       setError("Failed to load configuration. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   // Load data on component mount
//   useEffect(() => {
//     fetchConfig();
//   }, [fetchConfig]);

//   // Add this function to handle encrypted field editing
//   const toggleEncryptedField = (fieldName) => {
//     setEditingEncryptedFields(prev => ({
//       ...prev,
//       [fieldName]: !prev[fieldName]
//     }));
//   };

//   const handleEncryptedFieldChange = (fieldName, value) => {
//     setEncryptedFieldValues(prev => ({
//       ...prev,
//       [fieldName]: value
//     }));
//   };


//   const EncryptedField = ({ label, fieldName, placeholder }) => {
//     const isEditing = editingEncryptedFields[fieldName];

//     return (
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           {label}
//         </label>
//         {!isEditing ? (
//           <div className="flex items-center">
//             <div className="flex-1 p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
//               ••••••••••••••••
//             </div>
//             <button
//               type="button"
//               onClick={() => toggleEncryptedField(fieldName)}
//               className="ml-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//             >
//               Edit
//             </button>
//           </div>
//         ) : (
//           <div className="flex items-center">
//             <input
//               type="password"
//               value={encryptedFieldValues[fieldName] || ''}
//               onChange={(e) => handleEncryptedFieldChange(fieldName, e.target.value)}
//               placeholder={placeholder}
//               className="flex-1 p-2 border border-gray-300 rounded-md"
//             />
//             <button
//               type="button"
//               onClick={() => toggleEncryptedField(fieldName)}
//               className="ml-2 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
//             >
//               Cancel
//             </button>
//           </div>
//         )}
//       </div>
//     );
//   };

//   // Field change handlers that avoid unnecessary re-renders
//   const handleFieldChange = useCallback((fieldName, value) => {
//     setConfig((prev) => {
//       if (fieldName.includes(".")) {
//         const [section, property] = fieldName.split(".");
//         return {
//           ...prev,
//           [section]: {
//             ...prev[section],
//             [property]: value,
//           },
//         };
//       } else {
//         return {
//           ...prev,
//           [fieldName]: value,
//         };
//       }
//     });
//   }, []);

//   // Specialized handler for color changes
//   const handleColorChange = useCallback((colorName, value) => {
//     setConfig((prev) => ({
//       ...prev,
//       colors: {
//         ...prev.colors,
//         [colorName]: value,
//       },
//     }));
//   }, []);

//   // Save configuration
//   // const saveConfig = async (e) => {
//   //   e.preventDefault();

//   //   try {
//   //     setSaving(true);
//   //     const token = localStorage.getItem("token");
//   //     await axios.post("/api/config", config, {
//   //       headers: {
//   //         Authorization: `Bearer ${token}`,
//   //       },
//   //     });

//   //     setSuccess("Configuration saved successfully!");
//   //     setError(null);

//   //     setTimeout(() => {
//   //       setSuccess(null);
//   //     }, 3000);
//   //   } catch (err) {
//   //     console.error("Error saving configuration:", err);
//   //     setError("Failed to save configuration. Please try again.");
//   //   } finally {
//   //     setSaving(false);
//   //   }
//   // };
//   // Fixed saveConfig function in ConfigManagement.js
//   const saveConfig = async (e) => {
//     e.preventDefault();

//     try {
//       setSaving(true);
//       const token = localStorage.getItem("token");

//       // Create the config to save - start with all regular fields
//       const configToSave = { ...config };

//       // Handle encrypted field updates
//       Object.keys(encryptedFieldValues).forEach(fieldName => {
//         if (encryptedFieldValues[fieldName] && encryptedFieldValues[fieldName].trim()) {
//           const [section, field] = fieldName.split('.');
//           if (!configToSave[section]) configToSave[section] = {};
//           configToSave[section][field] = encryptedFieldValues[fieldName];
//         }
//       });

//       // ✅ IMPORTANT: Don't remove entire sections, just remove placeholders for encrypted fields
//       const encryptedFields = [
//         'stripe.secretKey',
//         'twilio.authToken',
//         'smtp.pass',
//         'openai.apiKey',
//         'uber.clientSecret',
//         'meta.pageAccessToken',
//         'meta.appSecret',
//         'canadaPost.password',
//         'apiKeys.googleMaps',
//         'zapier.webhookSecret'
//       ];

//       // Remove placeholder values for encrypted fields that weren't changed
//       encryptedFields.forEach(fieldPath => {
//         const [section, field] = fieldPath.split('.');
//         if (configToSave[section] && configToSave[section][field] === '***encrypted***') {
//           delete configToSave[section][field];
//         }
//       });

//       await axios.post("/api/config", configToSave, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       setSuccess("Configuration saved successfully!");
//       setError(null);
//       // Clear edited encrypted fields
//       setEncryptedFieldValues({});
//       setEditingEncryptedFields({});
//       setTimeout(() => setSuccess(null), 3000);
//     } catch (err) {
//       console.error("Error saving configuration:", err);
//       setError("Failed to save configuration. Please try again.");
//     } finally {
//       setSaving(false);
//     }
//   };

//   // Toggle section expansion
//   const toggleSection = useCallback((section) => {
//     setExpandedSection((prevExpanded) =>
//       prevExpanded === section ? null : section
//     );
//   }, []);

//   // Clear notification messages
//   const clearNotifications = useCallback(() => {
//     setError(null);
//     setSuccess(null);
//   }, []);

//   return (
//     <div className="space-y-6">
//       {/* Header */}
//       <h2 className="text-2xl font-semibold">Website Configuration</h2>

//       <div className="bg-gray-50 p-6 rounded-2xl">
//         {/* Notification area */}
//         <div className="container mx-auto px-4 py-2">
//           {error && (
//             <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex justify-between items-center">
//               <div className="flex items-center">
//                 <AlertCircle className="w-5 h-5 mr-2" />
//                 <p>{error}</p>
//               </div>
//               <button
//                 onClick={clearNotifications}
//                 className="text-red-500 hover:text-red-700"
//                 type="button"
//               >
//                 ×
//               </button>
//             </div>
//           )}

//           {success && (
//             <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex justify-between items-center">
//               <div className="flex items-center">
//                 <Check className="w-5 h-5 mr-2" />
//                 <p>{success}</p>
//               </div>
//               <button
//                 onClick={clearNotifications}
//                 className="text-green-500 hover:text-green-700"
//                 type="button"
//               >
//                 ×
//               </button>
//             </div>
//           )}
//         </div>

//         {loading ? (
//           <div className="h-64 flex justify-center items-center">
//             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
//           </div>
//         ) : (
//           <form onSubmit={saveConfig} className="space-y-4">
//             {/* Form header */}
//             <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
//               <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
//                 <h1 className="text-2xl font-semibold text-gray-800">
//                   Website Configuration
//                 </h1>
//                 <button
//                   type="submit"
//                   disabled={saving}
//                   className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
//                 >
//                   {saving ? (
//                     <>
//                       <Loader2 className="w-4 h-4 mr-2 animate-spin" />
//                       Saving...
//                     </>
//                   ) : (
//                     <>
//                       <Save className="w-4 h-4 mr-2" />
//                       Save All Settings
//                     </>
//                   )}
//                 </button>
//               </div>
//               <p className="text-gray-600 mt-2">
//                 Manage your website's settings, appearance, and behavior.
//                 Changes will be applied site-wide.
//               </p>
//             </div>

//             {/* Configuration Sections */}
//             <div className="grid grid-cols-1 gap-6">
//               {/* General Settings */}
//               <ConfigSection
//                 title="General Settings"
//                 icon={Globe}
//                 id="general"
//                 isExpanded={expandedSection === "general"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <TextField
//                       label="Website Name"
//                       name="siteName"
//                       value={config.siteName}
//                       onChange={handleFieldChange}
//                       required={true}
//                       helperText="This will appear in the browser tab and across the site"
//                     />

//                     <TextField
//                       label="Tagline"
//                       name="siteTagline"
//                       value={config.siteTagline}
//                       onChange={handleFieldChange}
//                       helperText="A short phrase describing your website"
//                     />

//                     <TextArea
//                       label="Site Description"
//                       name="siteDescription"
//                       value={config.siteDescription}
//                       onChange={handleFieldChange}
//                       helperText="A longer description of your website (used for SEO)"
//                     />
//                   </div>

//                   <div className="space-y-4">
//                     <TextField
//                       label="Site Keywords"
//                       name="siteKeywords"
//                       value={config.siteKeywords}
//                       onChange={handleFieldChange}
//                       helperText="Comma-separated keywords for SEO"
//                     />

//                     <FileUploadField
//                       label="Logo"
//                       name="logoUrl"
//                       value={config.logoUrl}
//                       onChange={handleFieldChange}
//                       accept="image/*"
//                       helperText="Upload your logo image (recommended: 200x200px, PNG/JPG)"
//                     />

//                     <FileUploadField
//                       label="Favicon"
//                       name="faviconUrl"
//                       value={config.faviconUrl}
//                       onChange={handleFieldChange}
//                       accept="image/x-icon,image/png"
//                       helperText="Upload your favicon (recommended: 32x32px, ICO or PNG)"
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Company Information */}
//               <ConfigSection
//                 title="Company Information"
//                 icon={Mail}
//                 id="companyInfo"
//                 isExpanded={expandedSection === "companyInfo"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <TextField
//                       label="Company Name"
//                       name="company.name"
//                       value={config.company?.name}
//                       onChange={handleFieldChange}
//                     />
//                     <TextField
//                       label="Company Phone"
//                       name="company.phone"
//                       value={config.company?.phone}
//                       onChange={handleFieldChange}
//                     />
//                     <TextField
//                       label="Company Email"
//                       name="company.email"
//                       value={config.company?.email}
//                       onChange={handleFieldChange}
//                       type="email"
//                     />
//                   </div>
//                   <div className="space-y-4">
//                     <TextField
//                       label="Address Line 1"
//                       name="company.addressLine1"
//                       value={config.company?.addressLine1}
//                       onChange={handleFieldChange}
//                     />
//                     <TextField
//                       label="City"
//                       name="company.city"
//                       value={config.company?.city}
//                       onChange={handleFieldChange}
//                     />
//                     <div className="grid grid-cols-2 gap-4">
//                       <TextField
//                         label="Province"
//                         name="company.province"
//                         value={config.company?.province}
//                         onChange={handleFieldChange}
//                       />
//                       <TextField
//                         label="Postal Code"
//                         name="company.postalCode"
//                         value={config.company?.postalCode}
//                         onChange={handleFieldChange}
//                       />
//                     </div>
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Environment Settings */}
//               <ConfigSection
//                 title="Environment Settings"
//                 icon={Globe}
//                 id="environmentSettings"
//                 isExpanded={expandedSection === "environmentSettings"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <SelectField
//                       label="Environment"
//                       name="environment.nodeEnv"
//                       value={config.environment?.nodeEnv}
//                       onChange={handleFieldChange}
//                       options={[
//                         { value: "development", label: "Development" },
//                         { value: "staging", label: "Staging" },
//                         { value: "production", label: "Production" }
//                       ]}
//                     />
//                     <SelectField
//                       label="Timezone"
//                       name="environment.timezone"
//                       value={config.environment?.timezone}
//                       onChange={handleFieldChange}
//                       options={[
//                         { value: "America/Vancouver", label: "America/Vancouver (PST)" },
//                         { value: "America/Toronto", label: "America/Toronto (EST)" },
//                         { value: "America/New_York", label: "America/New_York (EST)" },
//                         { value: "UTC", label: "UTC" }
//                       ]}
//                     />
//                   </div>
//                   <div className="space-y-4">
//                     <TextField
//                       label="Frontend URL"
//                       name="environment.frontendUrl"
//                       value={config.environment?.frontendUrl}
//                       onChange={handleFieldChange}
//                       helperText="Main website URL"
//                     />
//                     <TextField
//                       label="Client URL"
//                       name="environment.clientUrl"
//                       value={config.environment?.clientUrl}
//                       onChange={handleFieldChange}
//                       helperText="Client application URL"
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Store Location */}
//               <ConfigSection
//                 title="Store Location (Uber Delivery)"
//                 icon={MapPin}
//                 id="storeLocation"
//                 isExpanded={expandedSection === "storeLocation"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <TextField
//                       label="Store Address"
//                       name="store.address"
//                       value={config.store?.address}
//                       onChange={handleFieldChange}
//                     />
//                     <TextField
//                       label="City"
//                       name="store.city"
//                       value={config.store?.city}
//                       onChange={handleFieldChange}
//                     />
//                   </div>
//                   <div className="space-y-4">
//                     <TextField
//                       label="State/Province"
//                       name="store.state"
//                       value={config.store?.state}
//                       onChange={handleFieldChange}
//                     />
//                     <TextField
//                       label="Postal Code"
//                       name="store.postalCode"
//                       value={config.store?.postalCode}
//                       onChange={handleFieldChange}
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Pricing Information */}
//               <ConfigSection
//                 title="Cart & Pricing Information"
//                 icon={Money}
//                 id="pricing"
//                 isExpanded={expandedSection === "pricing"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="mb-4">
//                     <Checkbox
//                       label="Only Show Pricing Tiers In Users' Cart"
//                       name="onlyPricingTier"
//                       checked={config.onlyPricingTier}
//                       onChange={handleFieldChange}
//                       helperText="By Selecting, Only pricing tiers will be shown"
//                     />
//                   </div>

//                   <div className="mb-4">
//                     <TextField
//                       label="Business Order Count"
//                       name="businessOrderCount"
//                       value={config.businessOrderCount}
//                       onChange={handleFieldChange}
//                       type="number"
//                     />
//                   </div>

//                   <div className="mb-4">
//                     <Checkbox
//                       label="Charge Business Users for Account Verification"
//                       name="businessUserCharging"
//                       checked={config.businessUserCharging}
//                       onChange={handleFieldChange}
//                       helperText="If enabled, business users will be charged for account verification (refundable)"
//                     />
//                   </div>

//                   {config.businessUserCharging && (
//                     <div className="mb-4">
//                       <CurrencyField
//                         label="Business Verification Amount"
//                         name="businessUserChargingAmount"
//                         value={config.businessUserChargingAmount}
//                         onChange={handleFieldChange}
//                         helperText="This amount will be charged to business users for verification (refundable)"
//                       />
//                     </div>
//                   )}

//                   <div className="mb-4">
//                     <Checkbox
//                       label="Enable Wholesale Pricing for Business Accounts"
//                       name="businessWholesalePricing"
//                       checked={config.businessWholesalePricing}
//                       onChange={handleFieldChange}
//                       helperText="If enabled, business users will see wholesale prices in cart regardless of quantity"
//                     />
//                   </div>

//                   <div className="mb-4">
//                     <Checkbox
//                       label="Enable Uber Delivery for Items with Regular Price"
//                       name="uberDeliveryForItemsWithRegularPrice"
//                       checked={config.uberDeliveryForItemsWithRegularPrice}
//                       onChange={handleFieldChange}
//                       helperText="If enabled, items with regular price in the cart will be deliverd by Uber if delivery is selected"
//                     />
//                   </div>

//                   <div className="mb-4">
//                     <Checkbox
//                       label="Show Custom Popup After Order Placement"
//                       name="showOrderSuccessPopup"
//                       checked={config.showOrderSuccessPopup}
//                       onChange={handleFieldChange}
//                       helperText="Display a custom message popup after successful order placement"
//                     />
//                   </div>

//                   {config.showOrderSuccessPopup && (
//                     <div className="mb-4 ml-6">
//                       <TextArea
//                         label="Order Success Popup Content"
//                         name="orderSuccessPopupContent"
//                         value={config.orderSuccessPopupContent}
//                         onChange={handleFieldChange}
//                         rows={6}
//                         helperText="This content will be displayed in the popup after order placement. You can use HTML for formatting."
//                         placeholder="Thank you for your order! We'll process it shortly..."
//                       />
//                     </div>
//                   )}
//                 </div>



//               </ConfigSection>

//               {/* Contact Information */}
//               <ConfigSection
//                 title="Contact Information"
//                 icon={Mail}
//                 id="contact"
//                 isExpanded={expandedSection === "contact"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <IconTextField
//                       label="Contact Email"
//                       name="contactEmail"
//                       value={config.contactEmail}
//                       onChange={handleFieldChange}
//                       icon={Mail}
//                       type="email"
//                     />

//                     <IconTextField
//                       label="Contact Phone"
//                       name="contactPhone"
//                       value={config.contactPhone}
//                       onChange={handleFieldChange}
//                       icon={Phone}
//                     />
//                   </div>

//                   <div className="space-y-4">
//                     <TextArea
//                       label="Contact Address"
//                       name="contactAddress"
//                       value={config.contactAddress}
//                       onChange={handleFieldChange}
//                       icon={MapPin}
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Social Media */}
//               <ConfigSection
//                 title="Social Media"
//                 icon={Facebook}
//                 id="social"
//                 isExpanded={expandedSection === "social"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <IconTextField
//                       label="Facebook"
//                       name="socialLinks.facebook"
//                       value={config.socialLinks?.facebook}
//                       onChange={handleFieldChange}
//                       icon={Facebook}
//                       placeholder="https://facebook.com/yourpage"
//                     />

//                     <IconTextField
//                       label="Twitter"
//                       name="socialLinks.twitter"
//                       value={config.socialLinks?.twitter}
//                       onChange={handleFieldChange}
//                       icon={Twitter}
//                       placeholder="https://twitter.com/yourhandle"
//                     />

//                     <IconTextField
//                       label="Instagram"
//                       name="socialLinks.instagram"
//                       value={config.socialLinks?.instagram}
//                       onChange={handleFieldChange}
//                       icon={Instagram}
//                       placeholder="https://instagram.com/yourhandle"
//                     />
//                   </div>

//                   <div className="space-y-4">
//                     <IconTextField
//                       label="LinkedIn"
//                       name="socialLinks.linkedin"
//                       value={config.socialLinks?.linkedin}
//                       onChange={handleFieldChange}
//                       icon={Linkedin}
//                       placeholder="https://linkedin.com/company/yourcompany"
//                     />

//                     <IconTextField
//                       label="YouTube"
//                       name="socialLinks.youtube"
//                       value={config.socialLinks?.youtube}
//                       onChange={handleFieldChange}
//                       icon={Youtube}
//                       placeholder="https://youtube.com/c/yourchannel"
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Colors & Branding */}
//               <ConfigSection
//                 title="Colors & Branding"
//                 icon={Palette}
//                 id="colors"
//                 isExpanded={expandedSection === "colors"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <h4 className="font-medium text-gray-900">Brand Colors</h4>

//                     <ColorField
//                       label="Primary"
//                       colorName="primary"
//                       color={config.colors?.primary}
//                       onChange={handleColorChange}
//                     />

//                     <ColorField
//                       label="Secondary"
//                       colorName="secondary"
//                       color={config.colors?.secondary}
//                       onChange={handleColorChange}
//                     />

//                     <ColorField
//                       label="Accent"
//                       colorName="accent"
//                       color={config.colors?.accent}
//                       onChange={handleColorChange}
//                     />

//                     <ColorField
//                       label="Border"
//                       colorName="inputBorder"
//                       color={config.colors?.inputBorder}
//                       onChange={handleColorChange}
//                     />
//                   </div>

//                   <div className="space-y-4">
//                     <h4 className="font-medium text-gray-900">
//                       Text & Background Colors
//                     </h4>

//                     <ColorField
//                       label="Text Primary"
//                       colorName="textPrimary"
//                       color={config.colors?.textPrimary}
//                       onChange={handleColorChange}
//                     />

//                     <ColorField
//                       label="Text Secondary"
//                       colorName="textSecondary"
//                       color={config.colors?.textSecondary}
//                       onChange={handleColorChange}
//                     />

//                     <ColorField
//                       label="Background"
//                       colorName="background"
//                       color={config.colors?.background}
//                       onChange={handleColorChange}
//                     />

//                     <ColorField
//                       label="Background Alt"
//                       colorName="backgroundAlt"
//                       color={config.colors?.backgroundAlt}
//                       onChange={handleColorChange}
//                     />
//                   </div>
//                 </div>

//                 {/* Preview section for colors */}
//                 <div className="mt-6">
//                   <h4 className="font-medium text-gray-900 mb-3">
//                     Color Preview
//                   </h4>
//                   <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
//                     <div
//                       className="p-4 rounded-md text-white flex items-center justify-center h-20"
//                       style={{ backgroundColor: config.colors?.primary }}
//                     >
//                       Primary
//                     </div>
//                     <div
//                       className="p-4 rounded-md text-white flex items-center justify-center h-20"
//                       style={{ backgroundColor: config.colors?.secondary }}
//                     >
//                       Secondary
//                     </div>
//                     <div
//                       className="p-4 rounded-md text-white flex items-center justify-center h-20"
//                       style={{ backgroundColor: config.colors?.accent }}
//                     >
//                       Accent
//                     </div>
//                     <div
//                       className="p-4 rounded-md text-white flex items-center justify-center h-20"
//                       style={{ backgroundColor: config.colors?.inputBorder }}
//                     >
//                       Border
//                     </div>
//                     <div
//                       className="p-4 rounded-md flex items-center justify-center h-20"
//                       style={{
//                         backgroundColor: config.colors?.background,
//                         color: config.colors?.textPrimary,
//                       }}
//                     >
//                       Background / Text
//                     </div>
//                     <div
//                       className="p-4 rounded-md flex items-center justify-center h-20"
//                       style={{
//                         backgroundColor: config.colors?.backgroundAlt,
//                         color: config.colors?.textSecondary,
//                       }}
//                     >
//                       Background Alt / Text Secondary
//                     </div>
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Typography */}
//               <ConfigSection
//                 title="Typography"
//                 icon={Type}
//                 id="typography"
//                 isExpanded={expandedSection === "typography"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <SelectField
//                       label="Heading Font"
//                       name="typography.headingFont"
//                       value={config.typography?.headingFont}
//                       onChange={handleFieldChange}
//                       options={[
//                         { value: "Inter", label: "Inter" },
//                         { value: "Roboto", label: "Roboto" },
//                         { value: "Open Sans", label: "Open Sans" },
//                         { value: "Montserrat", label: "Montserrat" },
//                         { value: "Lato", label: "Lato" },
//                         { value: "Poppins", label: "Poppins" },
//                         {
//                           value: "Playfair Display",
//                           label: "Playfair Display",
//                         },
//                         { value: "Merriweather", label: "Merriweather" },
//                       ]}
//                     />

//                     <SelectField
//                       label="Body Font"
//                       name="typography.bodyFont"
//                       value={config.typography?.bodyFont}
//                       onChange={handleFieldChange}
//                       options={[
//                         { value: "Inter", label: "Inter" },
//                         { value: "Roboto", label: "Roboto" },
//                         { value: "Open Sans", label: "Open Sans" },
//                         { value: "Montserrat", label: "Montserrat" },
//                         { value: "Lato", label: "Lato" },
//                         { value: "Poppins", label: "Poppins" },
//                         { value: "Source Sans Pro", label: "Source Sans Pro" },
//                         { value: "Nunito", label: "Nunito" },
//                       ]}
//                     />
//                   </div>

//                   <div className="space-y-4">
//                     <SelectField
//                       label="Base Font Size"
//                       name="typography.baseSize"
//                       value={config.typography?.baseSize}
//                       onChange={handleFieldChange}
//                       options={[
//                         { value: "14px", label: "Small (14px)" },
//                         { value: "16px", label: "Medium (16px)" },
//                         { value: "18px", label: "Large (18px)" },
//                       ]}
//                     />
//                   </div>
//                 </div>

//                 <div className="mt-6">
//                   <h4 className="font-medium text-gray-900 mb-3">
//                     Typography Preview
//                   </h4>
//                   <div
//                     className="p-6 border border-gray-200 rounded-md"
//                     style={{
//                       fontFamily: `${config.typography?.bodyFont}, system-ui, sans-serif`,
//                       fontSize: config.typography?.baseSize,
//                     }}
//                   >
//                     <h1
//                       className="text-3xl mb-3"
//                       style={{
//                         fontFamily: `${config.typography?.headingFont}, system-ui, sans-serif`,
//                         color: config.colors?.textPrimary,
//                       }}
//                     >
//                       Heading Font: {config.typography?.headingFont}
//                     </h1>
//                     <p
//                       className="mb-4"
//                       style={{ color: config.colors?.textPrimary }}
//                     >
//                       Body Font: {config.typography?.bodyFont} at{" "}
//                       {config.typography?.baseSize} size. This is a preview of
//                       how text will appear on your website using your selected
//                       typography settings.
//                     </p>
//                     <p style={{ color: config.colors?.textSecondary }}>
//                       This is secondary text using your selected colors and
//                       fonts.
//                     </p>
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* SEO & Meta */}
//               <ConfigSection
//                 title="SEO & Meta"
//                 icon={Info}
//                 id="seo"
//                 isExpanded={expandedSection === "seo"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <TextField
//                       label="Default Meta Title"
//                       name="metaTitle"
//                       value={config.metaTitle}
//                       onChange={handleFieldChange}
//                       helperText="Defaults to Site Name if empty"
//                     />

//                     <TextArea
//                       label="Default Meta Description"
//                       name="metaDescription"
//                       value={config.metaDescription}
//                       onChange={handleFieldChange}
//                       helperText="Defaults to Site Description if empty"
//                     />
//                   </div>

//                   <div className="space-y-4">
//                     <TextField
//                       label="Google Analytics ID"
//                       name="googleAnalyticsId"
//                       value={config.googleAnalyticsId}
//                       onChange={handleFieldChange}
//                       placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
//                       helperText="Your Google Analytics tracking ID"
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* Feature Toggles */}
//               <ConfigSection
//                 title="Feature Toggles"
//                 icon={Settings}
//                 id="features"
//                 isExpanded={expandedSection === "features"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div>
//                     <Checkbox
//                       label="Enable Blog"
//                       name="features.enableBlog"
//                       checked={config.features?.enableBlog}
//                       onChange={handleFieldChange}
//                       helperText="Show blog posts section on website"
//                     />

//                     <Checkbox
//                       label="Enable Newsletter"
//                       name="features.enableNewsletter"
//                       checked={config.features?.enableNewsletter}
//                       onChange={handleFieldChange}
//                       helperText="Show newsletter signup forms"
//                     />
//                   </div>

//                   <div>
//                     <Checkbox
//                       label="Enable Search"
//                       name="features.enableSearch"
//                       checked={config.features?.enableSearch}
//                       onChange={handleFieldChange}
//                       helperText="Show search bar in header"
//                     />

//                     <Checkbox
//                       label="Enable Dark Mode"
//                       name="features.enableDarkMode"
//                       checked={config.features?.enableDarkMode}
//                       onChange={handleFieldChange}
//                       helperText="Allow users to toggle between light and dark themes"
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* API Keys & Integrations */}
//               <ConfigSection
//                 title="API Keys & Integrations"
//                 icon={Layout}
//                 id="apiKeys"
//                 isExpanded={expandedSection === "apiKeys"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <EncryptedField
//                       label="Google Maps API Key"
//                       fieldName="apiKeys.googleMaps"
//                       placeholder="Your Google Maps API key"
//                     />

//                     {/*
//                     <TextField
//                       label="Mailchimp API Key"
//                       name="apiKeys.mailchimp"
//                       value={config.apiKeys?.mailchimp}
//                       onChange={handleFieldChange}
//                       helperText="Required for newsletter functionality"
//                     /> */}
//                   </div>

//                   <div className="space-y-4">
//                     <EncryptedField
//                       label="Zapier Webhook Secret"
//                       fieldName="zapier.webhookSecret"
//                       placeholder="Your Zapier webhook secret"
//                     />
//                   </div>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <EncryptedField
//                     label="OpenAI API Key"
//                     fieldName="openai.apiKey"
//                     placeholder="sk-..."
//                   />
//                 </div>

//                 <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 rounded-md">
//                   <div className="flex">
//                     <Info className="h-5 w-5 mr-2" />
//                     <div>
//                       <p className="text-sm font-medium">Security Notice</p>
//                       <p className="text-xs mt-1">
//                         API keys are sensitive information. Ensure your server
//                         environment correctly handles these credentials
//                         securely.
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               </ConfigSection>


//               {/* Payment Integration */}
//               <ConfigSection
//                 title="Payment Integration"
//                 icon={Money}
//                 id="payment"
//                 isExpanded={expandedSection === "payment"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <TextField
//                     label="Stripe Public Key"
//                     name="stripe.publicKey"
//                     value={config.stripe?.publicKey}
//                     onChange={handleFieldChange}
//                   />
//                   <EncryptedField
//                     label="Stripe Secret Key"
//                     fieldName="stripe.secretKey"
//                     placeholder="sk_test_..."
//                   />
//                 </div>
//               </ConfigSection>

//               {/* Communication Services */}
//               <ConfigSection
//                 title="Communication Services"
//                 icon={Phone}
//                 id="communication"
//                 isExpanded={expandedSection === "communication"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <h4 className="font-medium text-gray-900">Twilio SMS</h4>
//                     <TextField
//                       label="Account SID"
//                       name="twilio.accountSid"
//                       value={config.twilio?.accountSid}
//                       onChange={handleFieldChange}
//                     />
//                     <EncryptedField
//                       label="Twilio Auth Token"
//                       fieldName="twilio.authToken"
//                       placeholder="Your Twilio auth token"
//                     />
//                     <TextField
//                       label="Phone Number"
//                       name="twilio.phoneNumber"
//                       value={config.twilio?.phoneNumber}
//                       onChange={handleFieldChange}
//                     />
//                   </div>
//                   <div className="space-y-4">
//                     <h4 className="font-medium text-gray-900">SMTP Email</h4>
//                     <TextField
//                       label="SMTP Host"
//                       name="smtp.host"
//                       value={config.smtp?.host}
//                       onChange={handleFieldChange}
//                     />
//                     <TextField
//                       label="SMTP User"
//                       name="smtp.user"
//                       value={config.smtp?.user}
//                       onChange={handleFieldChange}
//                     />
//                     <EncryptedField
//                       label="SMTP Password"
//                       fieldName="smtp.pass"
//                       placeholder="Your SMTP password"
//                     />
//                   </div>
//                 </div>
//               </ConfigSection>

//               {/* External APIs */}
//               {/* <ConfigSection
//                 title="External APIs"
//                 icon={Settings}
//                 id="external"
//                 isExpanded={expandedSection === "external"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <TextField
//                     label="OpenAI API Key"
//                     name="openai.apiKey"
//                     value={config.openai?.apiKey ? "***encrypted***" : ""}
//                     onChange={handleFieldChange}
//                     type="password"
//                   />
//                 </div>
//               </ConfigSection> */}

//               {/* Analytics & Tracking */}
//               <ConfigSection
//                 title="Analytics & Tracking"
//                 icon={BarChart3}
//                 id="analytics"
//                 isExpanded={expandedSection === "analytics"}
//                 onToggle={toggleSection}
//               >
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <TextField
//                       label="Google Tag Manager ID"
//                       name="analytics.gtagId"
//                       value={config.analytics?.gtagId}
//                       onChange={handleFieldChange}
//                       placeholder="GTM-XXXXXXX"
//                       helperText="Your Google Tag Manager container ID"
//                     />

//                     <TextField
//                       label="Google Analytics ID"
//                       name="analytics.gaId"
//                       value={config.analytics?.gaId}
//                       onChange={handleFieldChange}
//                       placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
//                       helperText="Your Google Analytics tracking ID"
//                     />
//                   </div>

//                   <div className="space-y-4">
//                     <TextField
//                       label="Conversion ID"
//                       name="analytics.conversionId"
//                       value={config.analytics?.conversionId}
//                       onChange={handleFieldChange}
//                       placeholder="AW-XXXXXXXXXX"
//                       helperText="Google Ads conversion tracking ID"
//                     />

//                     <TextField
//                       label="Conversion Label"
//                       name="analytics.conversionLabel"
//                       value={config.analytics?.conversionLabel}
//                       onChange={handleFieldChange}
//                       placeholder="XXXXXXXXXX/XXXXXXXXXX"
//                       helperText="Google Ads conversion label"
//                     />
//                   </div>
//                 </div>

//                 <div className="mt-4 p-4 bg-blue-50 text-blue-800 rounded-md">
//                   <div className="flex">
//                     <Info className="h-5 w-5 mr-2" />
//                     <div>
//                       <p className="text-sm font-medium">Analytics Setup</p>
//                       <p className="text-xs mt-1">
//                         These settings will be used to generate the appropriate tracking scripts in your index.html file.
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               </ConfigSection>

//             </div>

//             {/* <button
//               onClick={async () => {
//                 if (
//                   window.confirm(
//                     "Are you sure you want to migrate all images from MongoDB to the file system? This process cannot be undone and may take several minutes."
//                   )
//                 ) {
//                   try {
//                     console.log({
//                       type: "info",
//                       message:
//                         "Migration started. This may take several minutes...",
//                     });

//                     const response = await fetch("/api/admin/migrate-images", {
//                       method: "POST",
//                       headers: {
//                         "Content-Type": "application/json",
//                         Authorization: `Bearer ${localStorage.getItem(
//                           "token"
//                         )}`,
//                       },
//                     });

//                     const data = await response.json();

//                     if (response.ok) {
//                       console.log({
//                         type: "success",
//                         message:
//                           "Image migration completed successfully! Please refresh the page to see changes.",
//                       });
//                     } else {
//                       throw new Error(data.error || "Migration failed");
//                     }
//                   } catch (error) {
//                     console.error("Migration error:", error);
//                     console.log({
//                       type: "error",
//                       message: `Migration failed: ${error.message}`,
//                     });
//                   }
//                 }
//               }}
//               className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-200"
//             >
//               Migrate Images from MongoDB to File System
//             </button>

//             <button
//               onClick={async () => {
//                 if (
//                   window.confirm(
//                     "This will update your database to use the filesystem image paths. This operation is safe to run multiple times. Continue?"
//                   )
//                 ) {
//                   try {
//                     console.log({
//                       type: "info",
//                       message:
//                         "Database update started. This may take a few minutes...",
//                     });

//                     const response = await fetch(
//                       "/api/admin/update-image-references",
//                       {
//                         method: "POST",
//                         headers: {
//                           "Content-Type": "application/json",
//                           Authorization: `Bearer ${localStorage.getItem(
//                             "token"
//                           )}`,
//                         },
//                       }
//                     );

//                     const data = await response.json();

//                     if (response.ok) {
//                       console.log({
//                         type: "success",
//                         message: `Database updated successfully! Products: ${data.stats.productsUpdated}, Categories: ${data.stats.categoriesUpdated}, Parent Categories: ${data.stats.parentCategoriesUpdated}`,
//                       });
//                     } else {
//                       throw new Error(data.error || "Update failed");
//                     }
//                   } catch (error) {
//                     console.error("Database update error:", error);
//                     console.log({
//                       type: "error",
//                       message: `Update failed: ${error.message}`,
//                     });
//                   }
//                 }
//               }}
//               className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-200"
//             >
//               Update Database Image References
//             </button>
//             <p>
//               {" "}
//               how it works:
//               <br />
//               first comment the new model in product, categry & parent category
//               to include the img id in each model (using old implemenation).
//               <br />
//               then, press the migrate img from mongo to file system to do the
//               trasfer. <br />
//               last, comment the old img id filed in product model, parent and
//               category to let system save new img data in each model & press
//               update database
//             </p> */}

//             {/* Save Button */}
//             <div className="mt-8 flex justify-end">
//               <button
//                 type="submit"
//                 disabled={saving}
//                 className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
//               >
//                 {saving ? (
//                   <>
//                     <Loader2 className="w-5 h-5 mr-2 animate-spin" />
//                     Saving Configuration...
//                   </>
//                 ) : (
//                   <>
//                     <Save className="w-5 h-5 mr-2" />
//                     Save All Settings
//                   </>
//                 )}
//               </button>
//             </div>
//           </form>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ConfigManagement;



